<template>
    <vx-date-picker :mode="props.mode"
        class="p-0!"
        :timezone="timezone"
        :minDate="minDate"
        :model-value="date"
        :rules="rules"
        @update:model-value="emitModelChange($event)"
    >
    </vx-date-picker>
</template>

<script lang="ts" setup>
import { VxDatePicker } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import dayjs from '~/utils/dayjs';
import type { Dayjs } from 'dayjs';

interface DateTimePickerConfig {
    timeZone?: string;
    minDate?: Date | Dayjs;
    format?: string;
    stepping?: number;
}

interface Props {
    modelValue: Date | string | Dayjs | null;
    config?: DateTimePickerConfig;
    mode?: 'dateTime' | 'date' | 'time';
}

const props = withDefaults(defineProps<Props>(), {
    config: () => ({}),
    mode: 'dateTime',
});

const timezone = computed(() => props.config?.timeZone);
const minDate = computed(() => {
    const min = props.config?.minDate;
    if (min && typeof min === 'object' && 'toDate' in min) {
        return (min as Dayjs).toDate();
    }
    return min;
});
const format = computed(() => props.config?.format);
const rules = ref<Record<string, Record<string, number>> | undefined>();

watch(() => props.config?.stepping, () => {
    const stepping = props.config?.stepping;
    if (!stepping) {
        rules.value = undefined;
        return;
    }

    rules.value = {
        minutes: { interval: stepping },
    };
}, { immediate: true });

const date = computed(() => {
    if (props.modelValue === null || props.modelValue === undefined) {
        return undefined;
    } else if (typeof props.modelValue === 'string') {
        let parsed;

        if (timezone.value && format.value) {
            parsed = dayjs.tz(props.modelValue, format.value, timezone.value);
        } else if (format.value) {
            parsed = dayjs(props.modelValue, format.value);
        } else {
            parsed = dayjs(props.modelValue);
        }

        return parsed.isValid() ? parsed.toDate() : undefined;
    }

    return 'toDate' in props.modelValue ? (props.modelValue as Dayjs).toDate() : props.modelValue;
});

const emit = defineEmits<{
    'update:modelValue': [value: Date | string];
}>();

const emitModelChange = (event: Date) => {
    if (!event) {
        return;
    }

    if (timezone.value && format.value) {
        emit('update:modelValue', dayjs(event).tz(timezone.value).format(format.value));
    } else if (format.value) {
        emit('update:modelValue', dayjs(event).format(format.value));
    } else {
        emit('update:modelValue', event);
    }
};
</script>
